import Zone from "@insite/client-framework/Components/Zone";
import { getUrl } from "@insite/client-framework/ServerSideRendering";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    ExcludeFromNavigation,
    ExcludeFromSignInRequired,
    HideFooter,
    HideHeader,
    MetaDescription,
    MetaKeywords,
    OpenGraphImage,
    OpenGraphTitle,
    OpenGraphUrl,
    StructuredPageData,
} from "@insite/client-framework/Types/FieldDefinition";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import Modals from "@insite/content-library/Components/Modals";
import Page, { PageProps as MobiusPageProps } from "@insite/mobius/Page";
import prepareOrganizationStructuredPageDataWithId from "@tCentro/Custom/Utilities/prepareOrganizationStructuredPageDataCustom";
import React, { useEffect } from "react";
import { connect } from "react-redux";

export interface HomePageStyles {
    page?: MobiusPageProps;
}

export const homePageStyles: HomePageStyles = {};
const styles = homePageStyles;

type Props = PageProps;

const mapStateToProps = (state: ApplicationState) => ({
    websiteName: state.context.website.name,
    website: state.context.website,
});

type ConnectedProps = Props & ReturnType<typeof mapStateToProps>;

interface WebsiteStructuredDataProps {
    authority: string;
    name: string;
}

const getAuthority = (): string => {
    const url = IS_SERVER_SIDE ? getUrl()! : window.location;

    return `${url.protocol}//${url.host}`;
};

const getWebsiteStructuredData = ({ authority, name }: WebsiteStructuredDataProps) => ({
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${authority}/#website`,
    url: `${authority}/`,
    name,
    publisher: { "@id": `${authority}/#organization` },
    potentialAction: {
        "@type": "SearchAction",
        target: `${authority}/Search?query={search_term_string}`,
        "query-input": "required name=search_term_string",
    },
});

const HomePage: React.FC<ConnectedProps> = ({ id, websiteName, website }) => {
    useEffect(() => {
        window.scrollTo({
            top: 0,
            left: 0,
            behavior: "smooth",
        });
    }, []);

    const authority = getAuthority();

    const structuredData = getWebsiteStructuredData({
        authority,
        name: websiteName || website?.name,
    });

    const organizationStructuredData = website ? prepareOrganizationStructuredPageDataWithId(website) : undefined;

    return (
        <>
            <Page data-test-selector="homePage" {...styles.page}>
                <Zone contentId={id} zoneName="Content" requireRows />

                <Modals />
            </Page>
            {structuredData && (
                <script
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{
                        __html: JSON.stringify(structuredData),
                    }}
                />
            )}
            {organizationStructuredData && (
                <script
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{
                        __html: organizationStructuredData,
                    }}
                />
            )}
        </>
    );
};

const pageModule: PageModule = {
    component: connect(mapStateToProps)(HomePage),
    definition: {
        hasEditableTitle: true,
        hasEditableUrlSegment: true,
        pageType: "System",
        fieldDefinitions: [
            MetaKeywords,
            MetaDescription,
            OpenGraphTitle,
            OpenGraphUrl,
            OpenGraphImage,
            {
                ...StructuredPageData,
                tooltip:
                    "Code entered in this field will be added to the head of the home page. This field only supports JSON-LD data.",
            },
            HideHeader,
            HideFooter,
            ExcludeFromNavigation,
            ExcludeFromSignInRequired,
        ],
    },
};

export default pageModule;

/**
 * @deprecated Use string literal "HomePage" instead of this constant.
 */
export const HomePageContext = "HomePage";
